import React from 'react';

// Softened palette: red is now an ACCENT (CTAs, highlights), not full-bleed.
// Light heroes + neutrals + a calm secondary (teal) to break up the red.
export const RED = '#D32F2F';
export const RED_DARK = '#B71C1C';
export const INK = '#1F2733';
export const TEAL = '#0E9488'; // secondary accent
export const GREY = '#5B6472';
export const LIGHT = '#F6F8FA';
export const SOFT = '#FFF6F4'; // very light red tint (sparingly)
export const BORDER = '#E6E8EC';
// Unified with the home's body font so SEO pages match the rest of the marketing site.
export const FONT = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

export const wrap = { maxWidth: 1120, margin: '0 auto', padding: '0 20px' };

export const btnRed = {
  display: 'inline-block', background: RED, color: '#fff', fontWeight: 700, fontSize: 16,
  padding: '14px 28px', borderRadius: 12, textDecoration: 'none', boxShadow: '0 8px 22px rgba(211,47,47,.22)',
};

// A light, airy hero. `title` can include a red <Accent> word.
export function Hero({ breadcrumb, eyebrow, title, subtitle, badges, image, imageAlt, children }) {
  const textCol = (
    <>
      {eyebrow ? <div style={{ color: TEAL, fontWeight: 700, fontSize: 13, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 12 }}>{eyebrow}</div> : null}
      <h1 style={{ fontSize: 44, fontWeight: 800, color: INK, lineHeight: 1.12, margin: '0 0 14px', maxWidth: 800 }}>{title}</h1>
      {subtitle ? <p style={{ fontSize: 19, fontWeight: 500, color: GREY, margin: '0 0 26px', maxWidth: 640 }}>{subtitle}</p> : null}
      {children}
      {badges ? (
        <div style={{ marginTop: 26, display: 'flex', flexWrap: 'wrap', gap: '10px 22px' }}>
          {badges.map((b) => (
            <span key={b} style={{ fontSize: 14, fontWeight: 600, color: INK }}>
              <span style={{ color: TEAL, fontWeight: 800 }}>✓</span> {b}
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
  return (
    <section style={{ background: `radial-gradient(1200px 380px at 78% -8%, ${SOFT} 0%, rgba(255,246,244,0) 60%), linear-gradient(180deg, #FFFFFF 0%, ${LIGHT} 100%)`, borderBottom: `1px solid ${BORDER}` }}>
      <div style={{ ...wrap, padding: '54px 20px 58px' }}>
        {breadcrumb}
        {image ? (
          // Two-column hero: copy on the left, the trade photo on the right (wraps on mobile).
          <div style={{ display: 'flex', gap: 44, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 440px', minWidth: 300 }}>{textCol}</div>
            <div style={{ flex: '1 1 380px', minWidth: 280 }}>
              <img src={image} alt={imageAlt || title} width="640" height="400" decoding="async" style={{ width: '100%', height: 'auto', aspectRatio: '16 / 10', maxHeight: 380, objectFit: 'cover', borderRadius: 18, boxShadow: '0 18px 44px rgba(15,18,20,.16)' }} />
            </div>
          </div>
        ) : textCol}
      </div>
    </section>
  );
}

export function Accent({ children }) {
  return <span style={{ color: RED }}>{children}</span>;
}

// Breadcrumb that reads on a light hero.
export function Crumbs({ items }) {
  return (
    <nav style={{ fontSize: 13, color: GREY, marginBottom: 16 }}>
      {items.map((it, i) => (
        <span key={i}>
          {i > 0 ? <span style={{ margin: '0 8px', color: '#c3c8d0' }}>/</span> : null}
          {it.href ? <a href={it.href} style={{ color: GREY, textDecoration: 'none' }}>{it.label}</a> : <span style={{ color: INK }}>{it.label}</span>}
        </span>
      ))}
    </nav>
  );
}

// Dark CTA band (charcoal, not red) — a strong non-red block to balance the page.
export function CtaBand({ title, subtitle, ctaText, ctaHref }) {
  return (
    <section style={{ background: INK, color: '#fff' }}>
      <div style={{ ...wrap, padding: '46px 20px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 10px' }}>{title}</h2>
        {subtitle ? <p style={{ fontSize: 16, opacity: 0.82, margin: '0 0 22px' }}>{subtitle}</p> : null}
        <a href={ctaHref} style={btnRed}>{ctaText}</a>
      </div>
    </section>
  );
}
